/**
 * Domain model for MDV Services.
 *
 * Every entity is a plain, serialisable object so it can round-trip through
 * IndexedDB, the mock API layer and (later) a real Express/JSON backend
 * without any transformation step.
 */

export type ID = string
/** ISO-8601 timestamp, e.g. `2026-09-06T09:30:00.000Z` */
export type ISODateTime = string
/** Calendar date only, `YYYY-MM-DD` */
export type ISODate = string

/* -------------------------------------------------------------------------- */
/* People, ministries, teams                                                  */
/* -------------------------------------------------------------------------- */

export type PersonRole = 'admin' | 'scheduler' | 'viewer' | 'member'

export interface Person {
  id: ID
  firstName: string
  lastName: string
  email: string
  phone?: string
  role: PersonRole
  avatarColor: string
  /** Uploaded photo, if there is one. Initials are used when there is not. */
  avatarUrl?: string | null
  /** Set by an admin; what the Sunday team screen shows instead. */
  officialAvatarUrl?: string | null
  /** Denormalised for fast filtering in the scheduler matrix. */
  teamIds: ID[]
  notes?: string
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/** Ministry -> Team -> Position (e.g. Worship Team -> Band -> Bass Guitar). */
export interface Ministry {
  id: ID
  name: string
  description?: string
  color: string
  sortOrder: number
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export interface Team {
  id: ID
  ministryId: ID
  name: string
  description?: string
  sortOrder: number
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export interface Position {
  id: ID
  teamId: ID
  name: string
  /** How many volunteers this position normally needs per service. */
  defaultQuantity: number
  sortOrder: number
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/** Which people are eligible for which position, and how good they are at it. */
export interface TeamMembership {
  id: ID
  personId: ID
  teamId: ID
  positionIds: ID[]
  proficiency: 'training' | 'qualified' | 'lead'
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/* -------------------------------------------------------------------------- */
/* Scheduling                                                                 */
/* -------------------------------------------------------------------------- */

export type SchedulingStatus = 'confirmed' | 'pending' | 'declined' | 'unscheduled'

export interface SchedulingAssignment {
  id: ID
  planId: ID
  positionId: ID
  teamId: ID
  personId: ID | null
  status: SchedulingStatus
  /** Set when the volunteer accepts/declines, used for the audit trail. */
  respondedAt?: ISODateTime
  declineReason?: string
  notes?: string
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/** Dates a volunteer has declared themselves unavailable. */
export interface Blockout {
  id: ID
  personId: ID
  startDate: ISODate
  endDate: ISODate
  reason?: string
  repeatsAnnually: boolean
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/* -------------------------------------------------------------------------- */
/* Service plans                                                              */
/* -------------------------------------------------------------------------- */

export interface ServiceType {
  id: ID
  name: string
  ministryId: ID
  /** Team ids normally scheduled for this service type — drives the matrix. */
  teamIds: ID[]
  defaultStartTime: string // "09:30"
  /**
   * Schedules people straight to confirmed rather than asking them.
   * For rotas that are a duty rather than an invitation.
   */
  autoConfirm?: boolean
  sortOrder: number
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export type PlanStatus = 'draft' | 'published' | 'archived'

export interface Plan {
  id: ID
  serviceTypeId: ID
  title: string
  serviceDate: ISODate
  startTime: string // "09:30"
  status: PlanStatus
  seriesTitle?: string
  seriesArtworkColor?: string
  notes?: string
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export type PlanItemType =
  | 'header'
  | 'song'
  | 'sermon'
  | 'announcement'
  | 'prayer'
  | 'media'
  | 'transition'
  | 'custom'

/** Per-department technical notes attached to an item of the order of service. */
export interface TechNotes {
  audio?: string
  video?: string
  lighting?: string
  stage?: string
}

export interface PlanItem {
  id: ID
  planId: ID
  type: PlanItemType
  title: string
  description?: string
  /** Length in seconds; the timeline derives every start time from these. */
  durationSeconds: number
  sortOrder: number
  /** Only for `type === 'song'`. */
  songId?: ID
  arrangementId?: ID
  keyOverride?: string
  /** Person responsible on stage (speaker, worship leader, host…). */
  responsiblePersonId?: ID
  techNotes?: TechNotes
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/* -------------------------------------------------------------------------- */
/* Song library                                                               */
/* -------------------------------------------------------------------------- */

export type MusicalKey =
  | 'C' | 'C#' | 'Db' | 'D' | 'D#' | 'Eb' | 'E' | 'F' | 'F#' | 'Gb'
  | 'G' | 'G#' | 'Ab' | 'A' | 'A#' | 'Bb' | 'B'
  | 'Am' | 'Bm' | 'Cm' | 'Dm' | 'Em' | 'Fm' | 'Gm'

export interface Song {
  id: ID
  title: string
  artist: string
  author?: string
  ccliNumber?: string
  copyright?: string
  bpm: number
  originalKey: MusicalKey
  meter?: string
  themes: string[]
  /**
   * Plain text, and the only lyrics anything public ever sees.
   *
   * Derived from `lyricsRich` on save rather than typed separately — one source
   * of truth, and the derivation is what strips the team's cue lines out.
   */
  lyrics: string
  /**
   * The team's copy: HTML, with inline bold and italic, and cue lines.
   *
   * A cue is a block marked `data-cue` — "Solo voz principal", "Teye sings this
   * part". It lives in the document rather than in a separate list of comments
   * anchored to line numbers, because an anchor drifts the moment somebody
   * inserts a line above it, and an orphaned note is worse than none.
   *
   * Cues print on the team's PDF and are stripped from `lyrics`, so they cannot
   * reach the public app even if somebody exposes the song.
   */
  lyricsRich?: string
  /** ChordPro-ish chord sheet, rendered monospaced. */
  chordSheet?: string
  youtubeUrl?: string
  /** "Expose to Public App" toggle — gates /api/v1/public/songs. */
  isPublic: boolean
  lastScheduledDate?: ISODate
  timesScheduled: number
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export interface Arrangement {
  id: ID
  songId: ID
  name: string
  key: MusicalKey
  bpm: number
  /** e.g. ["Intro","Verse 1","Chorus","Bridge","Chorus","Outro"] */
  sequence: string[]
  lengthSeconds: number
  notes?: string
  isDefault: boolean
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export type MediaKind =
  | 'reference' | 'click' | 'instrumental' | 'vocal' | 'stem' | 'youtube' | 'other'

/** An audio file attached to a song, for rehearsal. */
export interface SongMedia {
  id: ID
  songId: ID
  /** Set when the track belongs to one specific arrangement. */
  arrangementId?: ID | null
  kind: MediaKind
  label: string
  /** Generated name on disk — never the uploader's filename. */
  fileName: string
  originalName: string
  mimeType: string
  sizeBytes: number
  durationSeconds?: number | null
  sortOrder: number
  /** `file` is uploaded audio; `link` is a practice video hosted elsewhere. */
  provider?: 'file' | 'link'
  /**
   * Where to play it from. On the server this is a real URL under /media/;
   * offline it is a blob: URL minted from the copy in IndexedDB.
   */
  url: string
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/** Server-reported upload ceilings, so the UI can reject files early. */
export interface UploadLimits {
  maxFileBytes: number
  maxPostBytes: number
  allowedExtensions: string[]
  allowedMimeTypes: string[]
  storageUsedBytes: number
}

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

export type NotificationType =
  | 'scheduling_request'
  | 'plan_update'
  | 'song_added'
  | 'general_alert'
  | 'blockout_conflict'

export type NotificationPriority = 'low' | 'normal' | 'high'

export interface AppNotification {
  id: ID
  type: NotificationType
  title: string
  body: string
  priority: NotificationPriority
  /** Recipient. `null` = broadcast to everyone. */
  personId: ID | null
  isRead: boolean
  /** True once an actionable notification has been accepted or declined. */
  actionResolved: boolean
  actionTaken?: 'accepted' | 'declined' | 'dismissed'
  /** Deep-link target inside the SPA, e.g. `/plans/abc`. */
  link?: string
  /** Context used by inline Accept/Decline buttons. */
  relatedPlanId?: ID
  relatedAssignmentId?: ID
  relatedSongId?: ID
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/* -------------------------------------------------------------------------- */
/* Admin / infrastructure                                                     */
/* -------------------------------------------------------------------------- */

export interface ApiKey {
  id: ID
  label: string
  /** Displayed masked everywhere except right after generation. */
  key: string
  scopes: Array<'songs:read' | 'arrangements:read' | 'plans:read'>
  lastUsedAt?: ISODateTime
  revoked: boolean
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

export interface AppSettings {
  id: 'app-settings'
  organizationName: string
  currentPersonId: ID
  timezone: string
  theme: 'light' | 'dark' | 'system'
  publicApiEnabled: boolean
  publicApiRequiresKey: boolean
  /**
   * Which service the church app features on its Songs screen.
   *
   * `null` means whichever service is next, across every type — the same rule
   * as the wall display's `service_type_id`. Set it and only that service
   * feeds `/public/upcoming`, so a midweek rehearsal cannot become what the
   * congregation sees.
   */
  publicServiceTypeId?: ID | null
  weekStartsOn: 0 | 1
  updatedAt: ISODateTime
}

/** Mutations recorded while offline, replayed by the background sync queue. */
export interface SyncQueueItem {
  id: ID
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  endpoint: string
  payload: unknown
  entity: string
  entityId: ID
  attempts: number
  lastError?: string
  status: 'queued' | 'syncing' | 'failed' | 'synced'
  createdAt: ISODateTime
  updatedAt: ISODateTime
}

/* -------------------------------------------------------------------------- */
/* API envelopes                                                              */
/* -------------------------------------------------------------------------- */

export interface ApiMeta {
  endpoint: string
  version: 'v1'
  generatedAt: ISODateTime
  count?: number
  [key: string]: unknown
}

export interface ApiResponse<T> {
  ok: true
  status: number
  meta: ApiMeta
  data: T
}

export interface ApiErrorBody {
  ok: false
  status: number
  error: string
  message: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/* -------------------------------------------------------------------------- */
/* View models (joined shapes used by the UI)                                 */
/* -------------------------------------------------------------------------- */

export interface PlanWithDetails extends Plan {
  serviceType?: ServiceType
  items: PlanItem[]
  assignments: SchedulingAssignment[]
  totalDurationSeconds: number
  /** Everyone who has said they are away on this plan's date. */
  blockouts?: PlanBlockout[]
}

export interface PlanBlockout {
  personId: ID
  reason?: string | null
  startDate: ISODate
  endDate: ISODate
  annual: boolean
}

export interface MatrixCell {
  planId: ID
  teamId: ID
  confirmed: number
  pending: number
  declined: number
  unfilled: number
  people: Array<{ personId: ID; positionId: ID; status: SchedulingStatus }>
}

export interface MatrixView {
  plans: Plan[]
  teams: Team[]
  cells: Record<string, MatrixCell>
}

export type LoadState = 'idle' | 'loading' | 'success' | 'error'
